import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getToken } from "@/lib/sessionStore";

import ScenarioLibrary from "./demo/ScenarioLibrary";
import TimelineRail from "./demo/TimelineRail";
import LiveKpisBar from "./demo/LiveKpisBar";
import { DEMO_COLORS, fmtBRL } from "./demo/demoStyles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MSGS_MS = 2000;
const POLL_TIMELINE_MS = 2200;
const POLL_KPIS_MS = 5000;
const POLL_RUN_MS = 2500;
const MIN_TYPING_MS = 2200;

/**
 * DemoSimulator — Fase 8B "Demo Comercial Autoguiada".
 *
 * Componentes visuais:
 *  - LiveKpisBar (topo)          → KPIs vivos do tenant
 *  - Chat central                → WhatsApp-style
 *  - TimelineRail (direita)      → eventos de negócio em tempo real
 *  - ScenarioLibrary (modal)     → biblioteca com "▶ Iniciar Demonstração"
 *  - Controles                   → Reiniciar conversa, parar auto-demo
 *
 * Fluxos:
 *  A) Manual — rep digita como cliente → IA responde.
 *  B) Auto — rep escolhe cenário → backend orquestra a sequência.
 */
export default function DemoSimulator() {
  const nav = useNavigate();

  // ── Estado principal ────────────────────────────────────────────
  const [customers, setCustomers] = useState([]);
  const [persona, setPersona] = useState("workshop");
  const [selectedId, setSelectedId] = useState("");
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [aiPending, setAiPending] = useState(false);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [typingUntil, setTypingUntil] = useState(0);

  // ── Estado de auto-demo ─────────────────────────────────────────
  const [scenarios, setScenarios] = useState([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [activeRun, setActiveRun] = useState(null);

  // ── Estado financeiro (cards) ───────────────────────────────────
  const [financialEvent, setFinancialEvent] = useState(null);

  const scrollerRef = useRef(null);
  const authHeader = useMemo(() => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  // ── Guarda demo (bloqueia acesso fora do modo demo) ─────────────
  useEffect(() => {
    const isDemo = localStorage.getItem("is_demo_mode") === "true";
    if (!isDemo) nav("/representante", { replace: true });
  }, [nav]);

  // ── Carrega tudo no boot ────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/demo/simulator/customers`, {
          headers: authHeader,
        });
        const data = r.data || {};
        setCustomers(data.customers || []);
        setPersona(data.persona || "workshop");
        setSelectedId(data.star_customer_id || data.customers?.[0]?.id || "");
      } catch (err) {
        console.error("[demo-sim] customers load failed", err);
        setError("Falha ao carregar clientes fictícios.");
      }
    })();

    // Carrega biblioteca de cenários uma vez.
    (async () => {
      setScenariosLoading(true);
      try {
        const r = await axios.get(`${API}/demo/scenarios`, { headers: authHeader });
        setScenarios(r.data?.scenarios || []);
      } catch (err) {
        console.error("[demo-sim] scenarios load failed", err);
      } finally {
        setScenariosLoading(false);
      }
    })();
  }, [authHeader]);

  // ── Poll: mensagens ─────────────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    if (!selectedId) return;
    try {
      const r = await axios.get(`${API}/demo/simulator/messages/${selectedId}`, {
        headers: authHeader,
      });
      const data = r.data || {};
      setMessages(data.messages || []);
      setAiPending(!!data.ai_pending);
      setConv(data.conversation || null);
    } catch (err) {
      // silencioso — polling
    }
  }, [selectedId, authHeader]);

  // ── Poll: timeline (incremental via ?since) ─────────────────────
  const fetchTimeline = useCallback(
    async (forceReset = false) => {
      if (!selectedId) return;
      try {
        const since =
          !forceReset && timeline.length > 0
            ? timeline[timeline.length - 1].created_at
            : null;
        const params = since ? `?since=${encodeURIComponent(since)}` : "";
        const r = await axios.get(`${API}/demo/timeline/${selectedId}${params}`, {
          headers: authHeader,
        });
        const newEvents = r.data?.events || [];
        if (forceReset) {
          setTimeline(newEvents);
        } else if (newEvents.length > 0) {
          setTimeline((prev) => [...prev, ...newEvents]);
          // Se apareceu evento de agendamento, dispara card financeiro.
          const appt = newEvents.find((e) => e.event === "appointment_created");
          if (appt) {
            triggerFinancialCard("appointment", appt);
          }
          const hot = newEvents.find((e) => e.event === "hot_lead_flagged");
          if (hot) triggerFinancialCard("hot_lead", hot);
        }
      } catch (err) {
        // silencioso
      }
    },
    [selectedId, timeline, authHeader],
  );

  // ── Poll: KPIs ──────────────────────────────────────────────────
  const fetchKpis = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/demo/kpis`, { headers: authHeader });
      setKpis(r.data || null);
    } catch (err) {
      // silencioso
    }
  }, [authHeader]);

  // ── Poll: status do run ativo ───────────────────────────────────
  const fetchRunStatus = useCallback(async () => {
    if (!activeRun?.run_id) return;
    try {
      const r = await axios.get(
        `${API}/demo/scenarios/status/${activeRun.run_id}`,
        { headers: authHeader },
      );
      setActiveRun(r.data);
      if (["completed", "cancelled", "error"].includes(r.data?.status)) {
        // deixa a UI mostrar o resultado por 4s e limpa
        setTimeout(() => setActiveRun(null), 4000);
      }
    } catch (err) {
      // run não encontrado ou erro — para o polling
      setActiveRun(null);
    }
  }, [activeRun, authHeader]);

  // ── Setup dos timers ────────────────────────────────────────────
  useEffect(() => {
    if (!selectedId) return;
    setMessages([]);
    setTimeline([]);
    setAiPending(false);
    fetchMessages();
    fetchTimeline(true);
    fetchKpis();
    const m = setInterval(fetchMessages, POLL_MSGS_MS);
    const t = setInterval(fetchTimeline, POLL_TIMELINE_MS);
    const k = setInterval(fetchKpis, POLL_KPIS_MS);
    return () => {
      clearInterval(m);
      clearInterval(t);
      clearInterval(k);
    };
  }, [selectedId]);

  useEffect(() => {
    if (!activeRun?.run_id) return;
    fetchRunStatus();
    const p = setInterval(fetchRunStatus, POLL_RUN_MS);
    return () => clearInterval(p);
  }, [activeRun?.run_id, fetchRunStatus]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, aiPending, typingUntil]);

  // ── Financial card ──────────────────────────────────────────────
  function triggerFinancialCard(kind, event) {
    let payload;
    if (kind === "appointment") {
      const ticket = persona === "dealer" ? 1450 : 890;
      payload = {
        kind,
        title: "Oportunidade concretizada",
        subtitle: "Agendamento criado pela IA",
        value_brl: ticket,
        note: "Estimativa ilustrativa baseada em ticket médio.",
      };
    } else if (kind === "hot_lead") {
      payload = {
        kind,
        title: "Lead quente identificado 🔥",
        subtitle: "IA priorizou e escalou pro humano",
        value_brl: persona === "dealer" ? 2200 : 1350,
        note: "Estimativa ilustrativa de oportunidade urgente.",
      };
    }
    if (!payload) return;
    setFinancialEvent(payload);
    setTimeout(() => setFinancialEvent(null), 6000);
  }

  // ── Envio manual "como cliente" ─────────────────────────────────
  async function sendAsCustomer() {
    const txt = inputText.trim();
    if (!txt || sending || !selectedId) return;
    if (activeRun?.status === "running" || activeRun?.status === "pending") {
      setError("Pare a demo automática antes de digitar manualmente.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const r = await axios.post(
        `${API}/demo/simulate-inbound`,
        { customer_id: selectedId, text: txt },
        { headers: authHeader },
      );
      setInputText("");
      const delay = r.data?.ai_typing_delay_ms || MIN_TYPING_MS;
      setTypingUntil(Date.now() + delay);
      fetchMessages();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.reason === "sim_rate_exceeded")
        setError(`Rate limit: máximo de ${detail.max_per_min} msgs/min.`);
      else if (detail?.reason === "text_too_long")
        setError("Mensagem muito longa (máx. 500 caracteres).");
      else setError("Erro ao enviar mensagem.");
    } finally {
      setSending(false);
    }
  }

  // ── Auto-demo controls ──────────────────────────────────────────
  async function startAutoDemo(scenarioCode) {
    setLibraryOpen(false);
    setError("");
    // Limpa a conversa antes de arrancar (garante estado limpo pro cenário).
    try {
      await axios.post(
        `${API}/demo/simulator/restart/${selectedId}`,
        {},
        { headers: authHeader },
      );
      setMessages([]);
      setTimeline([]);
      await fetchMessages();
    } catch (err) {
      // segue mesmo se falhar (talvez já estivesse limpo)
    }
    try {
      const r = await axios.post(
        `${API}/demo/scenarios/${scenarioCode}/start`,
        { customer_id: selectedId },
        { headers: authHeader },
      );
      setActiveRun(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const reason = detail?.reason || "";
      if (reason === "already_running_for_customer")
        setError("Já existe uma demo rodando para esse cliente.");
      else if (reason === "rep_max_runs_reached")
        setError("Limite de demos simultâneas atingido.");
      else setError(`Falha ao iniciar cenário: ${reason || "erro"}`);
    }
  }

  async function stopAutoDemo() {
    if (!activeRun?.run_id) return;
    try {
      await axios.post(
        `${API}/demo/scenarios/stop/${activeRun.run_id}`,
        {},
        { headers: authHeader },
      );
    } catch (err) {
      // ok
    }
  }

  async function restartConversation() {
    if (!selectedId) return;
    if (activeRun?.status === "running") {
      await stopAutoDemo();
    }
    try {
      await axios.post(
        `${API}/demo/simulator/restart/${selectedId}`,
        {},
        { headers: authHeader },
      );
      setMessages([]);
      setTimeline([]);
      setAiPending(false);
      setError("");
      fetchMessages();
      fetchKpis();
    } catch (err) {
      setError("Falha ao reiniciar a conversa.");
    }
  }

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendAsCustomer();
    }
  };

  const showTyping = aiPending || Date.now() < typingUntil;
  const selectedCustomer = customers.find((c) => c.id === selectedId);
  const runActive = !!(
    activeRun && ["running", "pending"].includes(activeRun.status)
  );

  return (
    <div
      data-testid="demo-simulator-page"
      style={{
        minHeight: "100vh",
        height: "100vh",
        background: DEMO_COLORS.bg,
        color: DEMO_COLORS.text,
        fontFamily: "system-ui, -apple-system, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Topbar ─────────────────────────────────────────────── */}
      <div
        data-testid="demo-simulator-topbar"
        style={{
          background: DEMO_COLORS.panel,
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${DEMO_COLORS.border}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            data-testid="demo-simulator-back-btn"
            onClick={() => nav("/app", { replace: true })}
            style={topBtnStyle}
          >
            ← Voltar
          </button>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              🎬 Simulador Comercial Autoguiado
            </div>
            <div style={{ fontSize: 10, color: DEMO_COLORS.textDim }}>
              Persona: {persona === "dealer" ? "Concessionária" : "Oficina"} · Dados
              fictícios · Zero envio real
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 11, color: DEMO_COLORS.textDim }}>Cliente:</label>
          <select
            data-testid="demo-sim-customer-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={runActive}
            style={{
              background: DEMO_COLORS.panelAlt,
              color: DEMO_COLORS.text,
              border: `1px solid ${DEMO_COLORS.divider}`,
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 12,
              minWidth: 200,
            }}
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            data-testid="demo-open-library-btn"
            onClick={() => setLibraryOpen(true)}
            disabled={!selectedId || runActive}
            style={{
              ...topBtnStyle,
              background: DEMO_COLORS.accent,
              color: "#000",
              fontWeight: 700,
            }}
          >
            ▶ Iniciar Demonstração
          </button>
          <button
            data-testid="demo-restart-conv-btn"
            onClick={restartConversation}
            disabled={!selectedId}
            style={topBtnStyle}
          >
            ↻ Reiniciar Conversa
          </button>
        </div>
      </div>

      {/* ── KPIs vivos ─────────────────────────────────────────── */}
      <LiveKpisBar kpis={kpis} />

      {/* ── Auto-demo banner ───────────────────────────────────── */}
      {activeRun && (
        <div
          data-testid="active-run-banner"
          style={{
            background:
              activeRun.status === "completed"
                ? "rgba(0, 168, 132, 0.2)"
                : activeRun.status === "error"
                  ? "rgba(239, 68, 68, 0.2)"
                  : activeRun.status === "cancelled"
                    ? "rgba(139, 92, 246, 0.2)"
                    : "rgba(245, 158, 11, 0.2)",
            padding: "8px 20px",
            fontSize: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${DEMO_COLORS.divider}`,
          }}
        >
          <div>
            <strong>{activeRun.scenario_title}</strong>
            {" · "}
            <span style={{ color: DEMO_COLORS.accentSoft }}>
              {activeRun.status.toUpperCase()}
            </span>
            {" · "}
            {activeRun.steps_done}/{activeRun.steps_total} passos
          </div>
          {runActive && (
            <button
              data-testid="stop-auto-demo-btn"
              onClick={stopAutoDemo}
              style={{
                ...topBtnStyle,
                background: DEMO_COLORS.danger,
                color: "#fff",
                fontWeight: 600,
              }}
            >
              ■ Parar demo
            </button>
          )}
        </div>
      )}

      {/* ── Main area: chat + timeline ─────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Chat column */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Chat header */}
          {selectedCustomer && (
            <div
              style={{
                background: DEMO_COLORS.panel,
                padding: "10px 20px",
                display: "flex",
                gap: 12,
                alignItems: "center",
                borderBottom: `1px solid ${DEMO_COLORS.border}`,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#6b7c85",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0b141a",
                }}
              >
                {(selectedCustomer.name || "?")
                  .split(" ")
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join("")}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {selectedCustomer.name}
                </div>
                <div style={{ fontSize: 10, color: DEMO_COLORS.textDim }}>
                  {selectedCustomer.phone || "—"} · {conv?.state || "sem conversa"}
                </div>
              </div>
            </div>
          )}

          {/* Chat scroller */}
          <div
            ref={scrollerRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "16px 40px",
              background: DEMO_COLORS.bg,
              position: "relative",
            }}
          >
            {messages.length === 0 && (
              <div
                data-testid="demo-sim-empty"
                style={{
                  color: DEMO_COLORS.textDim,
                  textAlign: "center",
                  marginTop: 40,
                  fontSize: 12,
                  lineHeight: 1.6,
                }}
              >
                Escolha um cenário no botão &ldquo;▶ Iniciar Demonstração&rdquo;
                <br />
                ou digite abaixo como se fosse o cliente.
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}
            {showTyping && <TypingBubble />}

            {/* Financial card overlay */}
            {financialEvent && <FinancialCard event={financialEvent} />}
          </div>

          {/* Input */}
          <div
            style={{
              background: DEMO_COLORS.panel,
              padding: "10px 20px",
              borderTop: `1px solid ${DEMO_COLORS.border}`,
            }}
          >
            {error && (
              <div
                data-testid="demo-sim-error"
                style={{
                  color: DEMO_COLORS.danger,
                  fontSize: 11,
                  marginBottom: 6,
                  textAlign: "center",
                }}
              >
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea
                data-testid="demo-sim-input"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={!selectedId || sending || runActive}
                rows={2}
                placeholder={
                  runActive
                    ? "Demo automática em execução — o AgentUS está conduzindo..."
                    : 'Digite como se fosse o cliente. Ex: "Bom dia! Meu carro tá..."'
                }
                style={{
                  flex: 1,
                  background: DEMO_COLORS.panelAlt,
                  color: DEMO_COLORS.text,
                  border: `1px solid ${DEMO_COLORS.divider}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontSize: 13,
                  fontFamily: "inherit",
                  resize: "none",
                  outline: "none",
                  opacity: runActive ? 0.5 : 1,
                }}
              />
              <button
                data-testid="demo-sim-send-btn"
                onClick={sendAsCustomer}
                disabled={!inputText.trim() || sending || !selectedId || runActive}
                style={{
                  background:
                    sending || !inputText.trim() || runActive
                      ? DEMO_COLORS.divider
                      : DEMO_COLORS.success,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 18px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor:
                    sending || !inputText.trim() || runActive
                      ? "not-allowed"
                      : "pointer",
                  minWidth: 90,
                }}
              >
                {sending ? "..." : "Enviar"}
              </button>
            </div>
          </div>
        </div>

        {/* Timeline rail */}
        <TimelineRail events={timeline} running={runActive} />
      </div>

      {/* Scenario library modal */}
      <ScenarioLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        scenarios={scenarios}
        loading={scenariosLoading}
        onSelect={startAutoDemo}
        disabled={!selectedId}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// Sub-componentes
// ═════════════════════════════════════════════════════════════════
const topBtnStyle = {
  background: "transparent",
  border: `1px solid ${DEMO_COLORS.divider}`,
  color: DEMO_COLORS.text,
  padding: "6px 14px",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
};

function MessageBubble({ msg }) {
  const isCustomer = msg.direction === "in";
  const bg = isCustomer ? DEMO_COLORS.bubbleIn : DEMO_COLORS.bubbleOut;
  const align = isCustomer ? "flex-end" : "flex-start";
  const author = isCustomer
    ? "Cliente (simulado)"
    : msg.author?.startsWith("user:")
      ? "Operador humano"
      : "AgentUS (IA)";
  const time = msg.created_at
    ? new Date(msg.created_at).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  return (
    <div
      data-testid={`demo-sim-msg-${msg.direction}`}
      style={{
        display: "flex",
        justifyContent: align,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          background: bg,
          color: DEMO_COLORS.text,
          padding: "8px 12px",
          borderRadius: 10,
          maxWidth: "72%",
          fontSize: 13,
          lineHeight: 1.5,
          boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 2 }}>{author}</div>
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {msg.body}
        </div>
        <div
          style={{
            fontSize: 9,
            opacity: 0.6,
            textAlign: "right",
            marginTop: 3,
          }}
        >
          {time}
        </div>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div
      data-testid="demo-sim-typing"
      style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}
    >
      <div
        style={{
          background: DEMO_COLORS.bubbleOut,
          color: DEMO_COLORS.textDim,
          padding: "8px 14px",
          borderRadius: 10,
          fontSize: 12,
          fontStyle: "italic",
        }}
      >
        AgentUS está digitando<span>...</span>
      </div>
    </div>
  );
}

function FinancialCard({ event }) {
  return (
    <div
      data-testid="financial-card"
      style={{
        position: "absolute",
        bottom: 24,
        right: 24,
        background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        color: "#000",
        padding: "14px 18px",
        borderRadius: 10,
        boxShadow: "0 12px 30px rgba(245, 158, 11, 0.4)",
        maxWidth: 300,
        animation: "slideInFromRight 400ms ease-out",
        zIndex: 10,
      }}
    >
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, opacity: 0.7 }}>
        {event.title}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4, lineHeight: 1.1 }}>
        {fmtBRL(event.value_brl)}
      </div>
      <div style={{ fontSize: 12, marginTop: 4, fontWeight: 500 }}>
        {event.subtitle}
      </div>
      <div
        style={{
          fontSize: 9,
          marginTop: 6,
          opacity: 0.65,
          fontStyle: "italic",
        }}
      >
        {event.note}
      </div>
    </div>
  );
}
